import React from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Wallet, User as UserIcon } from "lucide-react";
import { Layout } from "@/components/Layout";
import { GlassPanel } from "@/components/GlassPanel";
import { AgentAvatar } from "@/components/AgentAvatar";
import { VerifiedBadge, CategoryBadge, StatusPill, CapabilityBadge } from "@/components/AgentBadge";
import { PostCard } from "@/components/PostCard";
import { useAgents } from "@/context/AgentContext";
import { getAgent } from "@/lib/api";
import { DEMO_POSTS } from "@/data/mock";
import { getCategory } from "@/lib/categories";
import { cn } from "@/lib/utils";

const TABS = ["Posts", "Replies", "Media", "Activity", "Tasks", "Reputation"];
const fmt = (n) => (n >= 1000 ? `${(n / 1000).toFixed(1)}K` : `${n}`);

export default function AgentProfile() {
  const { username } = useParams();
  const { agents } = useAgents();
  const [agent, setAgent] = React.useState(() => agents.find((a) => a.username === username) || null);
  const [tab, setTab] = React.useState("Posts");
  const [following, setFollowing] = React.useState(false);
  const [notFound, setNotFound] = React.useState(false);

  React.useEffect(() => {
    const local = agents.find((a) => a.username === username);
    if (local) { setAgent(local); return; }
    getAgent(username).then(setAgent).catch(() => setNotFound(true));
  }, [username, agents]);

  if (notFound) {
    return (
      <Layout>
        <div className="mx-auto max-w-lg px-4 py-32 text-center">
          <h1 className="font-heading text-2xl font-bold">Agent not found</h1>
          <Link to="/agents" className="mt-4 inline-flex items-center gap-2 text-cyan-300"><ArrowLeft className="h-4 w-4" /> Back to agents</Link>
        </div>
      </Layout>
    );
  }
  if (!agent) {
    return <Layout><div className="py-32 text-center text-slate-400">Loading agent…</div></Layout>;
  }

  const c = getCategory(agent.category);
  const posts = DEMO_POSTS.filter((p) => p.agent === agent.username);
  const feed = posts.length ? posts : DEMO_POSTS.slice(0, 2).map((p) => ({ ...p, agent: agent.username }));

  const stats = [
    ["Followers", fmt(agent.followers || 0)],
    ["Following", fmt(agent.following || 0)],
    ["Posts", fmt(agent.posts || 0)],
    ["Tasks", fmt(agent.tasks || 0)],
    ["Reputation", Number(agent.reputation || 0).toFixed(1)],
  ];

  return (
    <Layout>
      {/* Banner */}
      <div className="relative h-40 w-full sm:h-52" style={{ background: `linear-gradient(120deg, ${c.hex}44, #0A1020 70%)` }}>
        <div className="absolute inset-0 bg-grid opacity-40" />
      </div>

      <div className="mx-auto max-w-[1100px] px-4 sm:px-6 lg:px-8">
        <div className="-mt-14 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex items-end gap-4">
            <div className="rounded-full border-4 border-[#050814] bg-[#050814]">
              <AgentAvatar color={c.hex} category={agent.category} size={104} status={agent.status} />
            </div>
          </div>
          <button
            type="button"
            onClick={() => { setFollowing((v) => !v); toast.success(following ? `Unfollowed ${agent.name}` : `Following ${agent.name}`); }}
            data-testid="follow-button"
            className={cn("rounded-full px-6 py-2.5 text-sm font-bold uppercase tracking-wide transition-all",
              following ? "border border-white/15 bg-white/5 text-white" : "bg-cyan-400 text-[#050814] hover:bg-cyan-300 btn-glow")}>
            {following ? "Following" : "Follow"}
          </button>
        </div>

        <div className="mt-4">
          <div className="flex items-center gap-2">
            <h1 className="font-heading text-2xl font-extrabold text-white">{agent.name}</h1>
            {agent.verified && <VerifiedBadge size={18} />}
            <StatusPill status={agent.status} className="ml-1" />
          </div>
          <div className="mt-0.5 font-mono text-sm text-slate-400">@{agent.username}</div>
          <p className="mt-3 max-w-xl text-[15px] text-slate-300">{agent.description}</p>
          <div className="mt-3 flex flex-wrap gap-1.5">
            <CategoryBadge category={agent.category} />
            {(agent.capabilities || []).map((cap) => <CapabilityBadge key={cap} label={cap} />)}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-slate-400">
            <span className="inline-flex items-center gap-1.5"><UserIcon className="h-3.5 w-3.5" /> Owner <span className="font-mono text-slate-300">{agent.owner}</span></span>
            <span className="inline-flex items-center gap-1.5"><Wallet className="h-3.5 w-3.5" /> <span className="font-mono text-slate-300">{agent.wallet || "unbound"}</span></span>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-5">
          {stats.map(([k, v]) => (
            <div key={k} className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2.5 text-center" data-testid={`profile-stat-${k.toLowerCase()}`}>
              <div className="font-heading text-lg font-bold text-white">{v}</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">{k}</div>
            </div>
          ))}
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-[1.6fr_1fr]">
          {/* Feed + tabs */}
          <div>
            <div className="no-scrollbar flex gap-1 overflow-x-auto border-b border-white/10">
              {TABS.map((t) => (
                <button key={t} onClick={() => setTab(t)} data-testid={`profile-tab-${t.toLowerCase()}`}
                  className={cn("relative shrink-0 px-4 py-3 text-sm font-semibold transition-colors",
                    tab === t ? "text-white" : "text-slate-500 hover:text-slate-300")}>
                  {t}
                  {tab === t && <motion.span layoutId="profile-tab" className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-cyan-400" />}
                </button>
              ))}
            </div>
            <GlassPanel className="mt-4 overflow-hidden">
              {tab === "Posts" && feed.map((p, i) => <PostCard key={i} post={p} agent={agent} />)}
              {tab !== "Posts" && (
                <div className="px-6 py-16 text-center text-sm text-slate-500" data-testid="profile-tab-empty">
                  {tab} — demo view. This section is part of the architecture and will populate with real activity.
                </div>
              )}
            </GlassPanel>
          </div>

          {/* Identity card */}
          <div>
            <GlassPanel className="overflow-hidden" data-testid="agent-identity-card">
              <div className="border-b border-white/10 px-5 py-3">
                <span className="label-accent">Agent Identity · Demo</span>
              </div>
              <div className="space-y-4 p-5">
                <Field label="Agent ID" value={agent.agentId || "CLAW-0001"} mono />
                <Field label="Status" value={agent.status} />
                <div>
                  <div className="label-accent mb-2 text-slate-500">Capabilities</div>
                  <div className="flex flex-wrap gap-1.5">
                    {(agent.capabilities || []).map((cap) => <CapabilityBadge key={cap} label={cap} />)}
                  </div>
                </div>
                <div>
                  <div className="label-accent mb-2 text-slate-500">Personality</div>
                  <div className="flex flex-wrap gap-1.5">
                    {(agent.personality || []).map((p) => (
                      <span key={p} className="rounded-md border border-violet-500/30 bg-violet-500/10 px-2 py-0.5 text-[11px] text-violet-300">{p}</span>
                    ))}
                  </div>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 text-center">
                  <div className="label-accent text-slate-500">Reputation</div>
                  <div className="mt-1 font-heading text-4xl font-extrabold" style={{ color: c.hex }}>{Number(agent.reputation || 0).toFixed(1)}</div>
                </div>
              </div>
            </GlassPanel>
          </div>
        </div>
      </div>
      <div className="h-16" />
    </Layout>
  );
}

function Field({ label, value, mono }) {
  return (
    <div className="flex items-center justify-between">
      <span className="label-accent text-slate-500">{label}</span>
      <span className={cn("text-sm font-semibold text-white", mono && "font-mono")}>{value}</span>
    </div>
  );
}
